import PlanSelector from '@/components/Pricing/PlanSelector';
import { INDUSTRIES } from '@/config/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaceIntegrations } from '@/contexts/AuthContext/useWorkspaceIntegrations';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { agentService } from '@common/api/agentService';
import { featureService } from '@common/api/featureService';
import bustlyLogo from '@common/assets/icons/logo.svg';
import {
    ArrowRight,
    CircleNotch,
    Plus,
    SignOut,
    WarningCircle,
    X
} from '@phosphor-icons/react';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IntegrationsSettings from '../Settings/components/IntegrationsSettings';
import IndustryRequestModal from './IndustryRequestModal';
import IntegrationRequestModal from './IntegrationRequestModal';
import InviteSelection from './InviteSelection';
import { useWorkspacePolling } from './useWorkspacePolling';

// --- Helper Components ---

const StepHeader = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <div className="text-center mb-8">
        <img src={bustlyLogo} alt="Bustly Logo" className="h-8 mx-auto mb-3" />
        <h2 className="text-3xl font-bold text-text-main mb-3">{title}</h2>
        <p className="text-text-sub text-lg max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
    </div>
);

// --- Step 1: Industry Selection ---

interface StepProps {
  onNext: () => void;
  onBack?: () => void;
  data: any;
  updateData: (data: any) => void;
  onComplete?: () => void;
}

const StepIndustry: React.FC<StepProps> = ({
    onNext, data, updateData 
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleSelect = (id: string) => {
        updateData({ industry: id });
        if (id !== 'other') {
            onNext();
        }
    };

    const handleOtherSubmit = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        if (data.otherIndustry?.trim()) {
            onNext();
        }
    };

    return (
        <div className="w-full max-w-5xl mx-auto px-6 pb-8">
            <StepHeader 
                title="What is your industry?" 
                subtitle="We'll tailor your experience and AI recommendations based on your business type."
            />
      
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                {INDUSTRIES.map((ind) => {
                    const Icon = ind.icon;
                    const isSelected = data.industry === ind.id;
                    const isOther = ind.id === 'other';

                    return (
                        <div
                            key={ind.id}
                            onClick={() => handleSelect(ind.id)}
                            className={clsx(
                                "glass-card flex items-start text-left p-4 rounded-xl transition-all duration-300 group relative overflow-hidden cursor-pointer",
                                isSelected 
                                    ? "border-primary bg-primary/5 shadow-md ring-1 ring-primary" 
                                    : "bg-white border border-gray-200 shadow-sm hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:-translate-y-1 hover:z-[10] hover:border-gray-300"
                            )}
                        >
                            <div
                                className={clsx(
                                    "p-2 rounded-lg shrink-0 mr-3 transition-colors",
                                    isSelected ? "bg-primary text-white" : "bg-gray-50 text-text-main group-hover:bg-primary/10 group-hover:text-primary"
                                )}
                            >
                                <Icon size={24} weight="bold" />
                            </div>
                            <div className="w-full min-w-0">
                                <h3 className={clsx("font-semibold text-base mb-0.5", isSelected ? "text-primary" : "text-text-main")}>
                                    {ind.label}
                                </h3>
                
                                {isOther && isSelected ? (
                                    <div className="mt-1.5 flex items-center gap-2 animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                                        <input
                                            type="text"
                                            autoFocus={false}
                                            placeholder="Type your industry..."
                                            value={data.otherIndustry || ''}
                                            onChange={(e) => updateData({ otherIndustry: e.target.value })}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleOtherSubmit(e);
                                                }
                                            }}
                                            className="w-full bg-white border border-primary/30 rounded-lg px-2.5 py-1.5 text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-primary/20 placeholder:text-text-sub/50"
                                        />
                                        <button
                                            onClick={handleOtherSubmit}
                                            disabled={!data.otherIndustry?.trim()}
                                            className="p-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ArrowRight size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-xs text-text-sub leading-snug line-clamp-2">{ind.desc}</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="w-full bg-[#F4F4F5] rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
                <div className="relative z-10 text-center md:text-left">
                    <h3 className="text-lg font-bold text-text-main mb-1">Don't see your industry?</h3>
                    <p className="text-text-sub text-sm max-w-xl">Bustly works with any business that runs on data. Let's talk about your specific needs.</p>
                </div>
         
                <button 
                    onClick={() => setIsModalOpen(true)}
                    className="px-6 py-2 bg-white border border-gray-200 text-text-main font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-sm flex items-center gap-2 whitespace-nowrap text-sm"
                >
                    Talk to Sales
                    <ArrowRight size={16} />
                </button>
            </div>

            <IndustryRequestModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
};

// --- Step 2: Business Profile ---

const StepProfile: React.FC<StepProps> = ({
    onNext, onBack, data, updateData
}) => {
    const [name, setName] = useState(data.businessName || '');
    const [logoPreview, setLogoPreview] = useState<string | null>(data.logoUrl || null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        setIsLoading(true);
        updateData({ businessName: name });

        try {
            // Create workspace with already uploaded logo URL
            const workspace = await featureService.createWorkspace({
                name: name.trim(),
                logoUrl: data.logoUrl,
                settings: { industry: data.industry === 'other' ? (data.otherIndustry || 'Other') : data.industry }
            });

            // Store workspace ID in onboarding data for later use
            updateData({ workspaceId: workspace.id });
            // Allow a brief moment for success feedback
            setIsLoading(false);
            onNext();
        } catch (error) {
            console.error('创建 workspace 失败:', error);
            setIsLoading(false);
            // You might want to show an error message to the user here
        }
    };

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Show preview immediately
            const previewUrl = URL.createObjectURL(file);
            setLogoPreview(previewUrl);
            setIsUploadingLogo(true);

            try {
                // Upload immediately
                const uploadResponse = await agentService.uploadFile(file);
                if (uploadResponse.success && uploadResponse.file_url) {
                    // Store the uploaded URL
                    updateData({ logoUrl: uploadResponse.file_url });
                } else {
                    throw new Error('Upload failed');
                }
            } catch (error) {
                console.error('Logo 上传失败:', error);
                // Remove preview on upload failure
                setLogoPreview(null);
                // Optionally show error to user
            } finally {
                setIsUploadingLogo(false);
            }
        }
    };

    const removeLogo = (e: React.MouseEvent) => {
        e.preventDefault();
        setLogoPreview(null);
        updateData({ logoUrl: null });
    };

    return (
        <div className="w-full max-w-lg mx-auto px-6">
            <StepHeader 
                title="Tell us about your business" 
                subtitle="This helps us personalize your dashboard branding."
            />

            <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                    <label className="block text-sm font-semibold text-text-main mb-2">
                        Business Name 
                        {' '}
                        <span className="text-red-500">*</span>
                    </label>
                    <input 
                        type="text" 
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-3 text-lg border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all shadow-sm"
                        placeholder="e.g. Acme Corp"
                        autoFocus
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-text-main mb-3">
                        Business Logo 
                        {' '}
                        <span className="text-text-sub font-normal">(Optional)</span>
                    </label>
          
                    <div className="flex items-center gap-4">
                        {logoPreview ? (
                            <div className="relative group">
                                <div className="w-16 h-16 rounded-xl border border-gray-200 p-1 bg-white shadow-sm flex items-center justify-center relative">
                                    {isUploadingLogo ? (
                                        <div className="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center z-10">
                                            <CircleNotch size={20} weight="bold" className="animate-spin text-primary" />
                                        </div>
                                    ) : null}
                                    <img src={logoPreview} alt="Logo" className="w-full h-full object-contain rounded-lg" />
                                </div>
                                {!isUploadingLogo && (
                                    <button
                                        onClick={removeLogo}
                                        className="absolute -top-2 -right-2 bg-white text-text-sub border border-gray-200 rounded-full p-1 shadow-sm hover:text-red-500 hover:border-red-100 transition-all"
                                        title="Remove logo"
                                    >
                                        <X size={12} weight="bold" />
                                    </button>
                                )}
                            </div>
                        ) : (
                            <label className="w-16 h-16 flex items-center justify-center rounded-xl border border-dashed border-gray-300 text-text-sub hover:border-primary hover:text-primary hover:bg-primary/5 transition-all cursor-pointer bg-gray-50/50">
                                <input type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                                {isUploadingLogo ? (
                                    <CircleNotch size={20} weight="bold" className="animate-spin text-primary" />
                                ) : (
                                    <Plus size={20} weight="bold" />
                                )}
                            </label>
                        )}
             
                        <div className="flex flex-col justify-center">
                            {!logoPreview ? (
                                <label className="cursor-pointer">
                                    <span className="text-sm font-semibold text-text-main hover:text-primary transition-colors">Upload a file</span>
                                    <input type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                                    <p className="text-xs text-text-sub mt-0.5">JPG, PNG</p>
                                </label>
                            ) : (
                                <div className="text-sm">
                                    <p className="font-medium text-text-main">Logo uploaded</p>
                                    <button onClick={removeLogo} className="text-xs text-red-500 hover:underline mt-0.5">Remove</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-auto pt-6 border-t border-gray-100 flex items-center justify-between">
                    <button 
                        type="button" 
                        onClick={onBack}
                        className="px-6 py-3 text-text-sub font-semibold rounded-xl hover:bg-gray-100 hover:text-text-main transition-colors"
                    >
                        Back
                    </button>
                    <button 
                        type="submit"
                        disabled={!name.trim() || isLoading}
                        className="px-8 py-3 bg-text-main text-white font-bold rounded-xl hover:bg-text-main/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2"
                    >
                        {isLoading ? <CircleNotch className="animate-spin" weight="bold" /> : "Continue"}
                        {!isLoading && <ArrowRight size={18} weight="bold" />}
                    </button>
                </div>
            </form>
        </div>
    );
};

const StepConnect: React.FC<StepProps> = ({
    onNext, data, onBack, updateData
}) => {
    // Show fixed recommendations for all industries
    const [isModalOpen, setIsModalOpen] = useState(false);
    const workspaceIntegrations = useWorkspaceIntegrations(data.workspaceId);
    const hasConnected = workspaceIntegrations.allIntegrations.some(i => i.connected);

    return (
        <div className="w-full max-w-4xl mx-auto px-6">
            <StepHeader
                title="Connect your data"
                subtitle={`Recommended integrations for ${data.industry === 'other' ? (data.otherIndustry || 'your business') : INDUSTRIES.find(i => i.id === data.industry)?.label || 'your business'}.`}
            />
            <IntegrationsSettings workspaceIntegrations={workspaceIntegrations} mode="onboarding" />

            <div className="mt-auto pt-6 border-t border-gray-100 flex items-center justify-between mb-8">
                {!hasConnected && !workspaceIntegrations.isLoading ? (
                    <div className="mt-auto pt-6 border-t border-gray-100 flex items-center justify-start">
                        <button
                            onClick={onBack}
                            className="px-6 py-3 text-gray-500 font-semibold rounded-xl hover:bg-gray-100 hover:text-gray-900 transition-colors flex items-center gap-2"
                        >
                            <ArrowRight size={18} className="rotate-180" weight="bold" />
                            Back
                        </button>
                    </div>
                ) : <div></div>}
                <button
                    onClick={onNext}
                    disabled={!hasConnected}
                    className={clsx(
                        "px-8 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2",
                        hasConnected
                            ? "bg-text-main text-white hover:bg-text-main/90"
                            : "bg-gray-100 text-text-sub cursor-not-allowed"
                    )}
                >
                    Continue
                    <ArrowRight size={18} weight="bold" />
                </button>
            </div>



            <IntegrationRequestModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
};

// --- Step 4: Plan Selection ---

const StepPlan: React.FC<StepProps> = ({
    onNext, onBack, data, updateData
}) => {
    const [status, setStatus] = useState<'idle' | 'processing' | 'error'>('idle');

    return (
        <div className="w-full max-w-5xl mx-auto px-6 h-full flex flex-col">
            <StepHeader
                title="Choose your plan"
                subtitle="Start your 7-day free trial. Auto-billed after trial. Cancel anytime."
            />

            {status === 'error' && (
                <div className="max-w-4xl mx-auto w-full mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                    <WarningCircle size={20} className="shrink-0" weight="bold" />
                    <div>
                        <p className="font-semibold">Payment failed</p>
                        <p className="text-sm">We couldn't process your card. Please try again.</p>
                    </div>
                    <button onClick={() => setStatus('idle')} className="ml-auto text-sm font-semibold hover:underline">Dismiss</button>
                </div>
            )}

            <PlanSelector
                mode="onboarding"
                workspaceId={data.workspaceId}
                className="mb-8"
            />

            <div className="mt-auto pt-6 border-t border-gray-100 flex items-center justify-start">
                <button
                    onClick={onBack}
                    className="px-6 py-3 text-gray-500 font-semibold rounded-xl hover:bg-gray-100 hover:text-gray-900 transition-colors flex items-center gap-2"
                >
                    <ArrowRight size={18} className="rotate-180" weight="bold" />
                    Back
                </button>
            </div>
        </div>
    );
};

// --- Main Container ---

const OnboardingOverlay: React.FC = () => {
    const navigate = useNavigate();
    const {
        nextStep, prevStep, stopOnboarding, onboardingData, updateOnboardingData
    } = useOnboarding();

    const currentStep = onboardingData.currentStep ?? 0;
    const {
        user, workspaceMembers, logout, workspaces, switchWorkspace
    } = useAuth();
    const { invitations } = workspaceMembers;
    const [isStepVisible, setIsStepVisible] = useState(false);
    const [userChoseCreate, setUserChoseCreate] = useState(false);

    // 使用自定义 hook 处理 workspace 轮询检查
    useWorkspacePolling();

    // 判断是新用户还是 create workspace 用户
    const isNewUser = !workspaces || workspaces.length === 0;


    // Transition state
    const [displayStep, setDisplayStep] = useState(currentStep);
    const [isTransitioning, setIsTransitioning] = useState(false);

    useEffect(() => {
        if (currentStep !== displayStep) {
            setIsTransitioning(true);
            const timer = setTimeout(() => {
                setDisplayStep(currentStep);
                requestAnimationFrame(() => {
                    setIsTransitioning(false);
                });
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [currentStep, displayStep]);

    useEffect(() => {
        setIsStepVisible(false);
        const raf = requestAnimationFrame(() => setIsStepVisible(true));
        return () => cancelAnimationFrame(raf);
    }, [currentStep]);

    // 判断是否显示邀请选择页面
    const showInviteSelection = invitations && invitations.length > 0 && !userChoseCreate;

    const steps = [
        <StepIndustry key="industry" onNext={nextStep} data={onboardingData} updateData={updateOnboardingData} />,
        <StepProfile key="profile" onNext={nextStep} onBack={prevStep} data={onboardingData} updateData={updateOnboardingData} />,
        <StepConnect key="connect" onNext={nextStep} onBack={prevStep} data={onboardingData} updateData={updateOnboardingData} />,
        <StepPlan
            key="plan"
            onNext={nextStep}
            onBack={prevStep}
            data={onboardingData}
            updateData={updateOnboardingData}
        />
    ];

    return (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-[#F8F9FB]">
            {/* Progress Bar - 只在 onboarding 流程中显示 */}
            {!showInviteSelection && (
                <div className="fixed top-0 left-0 w-full h-1.5 bg-gray-100 z-[101]">
                    <div
                        className="h-full bg-text-main transition-all duration-500 ease-out"
                        style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                    />
                </div>
            )}

            {/* Content Container */}
            <div className="min-h-screen w-full flex flex-col pt-16 pb-12">
             
                {/* Header Actions */}
                <div className="fixed top-6 right-6 z-[102]">
                    {isNewUser ? (
                        // 新用户：显示 Log out 按钮
                        <button
                            onClick={logout}
                            className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-full shadow-sm hover:bg-white hover:shadow-md transition-all group"
                        >
                            <SignOut size={16} className="text-gray-500 group-hover:text-gray-800" weight="bold" />
                            <span className="text-sm font-medium text-gray-500 group-hover:text-gray-800">Log out</span>
                        </button>
                    ) : (
                        // Create workspace 用户：显示关闭按钮
                        <button
                            onClick={() => {
                                stopOnboarding();
                                navigate('/');
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-full shadow-sm hover:bg-white hover:shadow-md transition-all group"
                        >
                            <X size={16} className="text-gray-500 group-hover:text-gray-800" weight="bold" />
                            <span className="text-sm font-medium text-gray-500 group-hover:text-gray-800">Close</span>
                        </button>
                    )}
                </div>

                {/* Step Content */}
                <div className="flex-1 flex flex-col items-center justify-center w-full relative">
                    {showInviteSelection ? (
                        <InviteSelection invitations={invitations} onChooseCreate={() => setUserChoseCreate(true)} />
                    ) : (
                        <div
                            className={clsx(
                                "w-full transition-all duration-300 ease-in-out transform",
                                isTransitioning
                                    ? "opacity-0 translate-y-4 scale-[0.98]"
                                    : "opacity-100 translate-y-0 scale-100"
                            )}
                        >
                            {steps[displayStep]}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingOverlay;
